import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import PortalChrome from '../../components/portal/PortalChrome';
import { CloseX, ignoreBackdropEscapeClose } from '../../components/portal/portalUi';
import { PortalProfileProvider, usePortalProfile } from '../../contexts/PortalProfileContext';
import { PortalPoshakaProvider } from '../../contexts/PortalPoshakaContext';
import { useAuth } from '../../contexts/AuthContext';
import { get } from '../../utils/api';

function PortalLoadingGate({ children, blocking = false }) {
  const { detailsLoading } = usePortalProfile();
  const open = detailsLoading || blocking;
  return (
    <>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
          <CloseX onClick={() => {}} disabled title="Please wait" />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={28} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Loading your account…</Typography>
              <Typography variant="body2" color="text.secondary">Please wait while we retrieve your information</Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {!open ? children : null}
    </>
  );
}

export default function PortalLayout() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { staff } = useAuth();
  const [welcomeCheckLoading, setWelcomeCheckLoading] = React.useState(true);
  const [welcomeAccepted, setWelcomeAccepted] = React.useState(null);
  const navDisabled = location.pathname === `/devotees/${id}/welcome`;
  const justAcceptedWelcome = !!location.state?.portalWelcomeJustAccepted;

  useEffect(() => {
    if (!staff?.subject || !id) return;
    if (String(id) !== String(staff.subject)) {
      navigate(`/devotees/${staff.subject}/home`, { replace: true });
    }
  }, [staff?.subject, id, navigate]);

  useEffect(() => {
    if (justAcceptedWelcome) {
      setWelcomeAccepted(true);
      setWelcomeCheckLoading(false);
    }
  }, [justAcceptedWelcome]);

  useEffect(() => {
    let cancelled = false;
    if (!staff?.subject) {
      setWelcomeCheckLoading(false);
      setWelcomeAccepted(true);
      return undefined;
    }
    if (justAcceptedWelcome) {
      return undefined;
    }
    (async () => {
      try {
        setWelcomeCheckLoading(true);
        const res = await get('/portal/me/preferences');
        if (cancelled) return;
        const acceptedAt = res?.data?.portalWelcomeAcceptedAt || null;
        setWelcomeAccepted(!!acceptedAt);
      } catch {
        if (!cancelled) setWelcomeAccepted(true);
      } finally {
        if (!cancelled) setWelcomeCheckLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staff?.subject, justAcceptedWelcome]);

  useEffect(() => {
    if (welcomeCheckLoading || welcomeAccepted == null || !staff?.subject || !id) return;
    const welcomePath = `/devotees/${staff.subject}/welcome`;
    const homePath = `/devotees/${staff.subject}/home`;
    const currentPath = location.pathname;

    if (!welcomeAccepted && currentPath !== welcomePath) {
      navigate(welcomePath, { replace: true });
      return;
    }
    if (welcomeAccepted && currentPath === welcomePath) {
      navigate(homePath, { replace: true });
    }
  }, [welcomeCheckLoading, welcomeAccepted, staff?.subject, id, location.pathname, navigate]);

  return (
    <PortalProfileProvider>
      <PortalPoshakaProvider>
        <PortalLoadingGate blocking={welcomeCheckLoading}>
          <PortalChrome navDisabled={navDisabled}>
            <Outlet />
          </PortalChrome>
        </PortalLoadingGate>
      </PortalPoshakaProvider>
    </PortalProfileProvider>
  );
}
